/**
 * Context Generator Service
 * Generates comprehensive prompts to continue work in a new chat
 */

import { roadmapData } from '../roadmapData';

/**
 * Generate a complete context prompt for continuing work with a new agent
 */
export const generateContextPrompt = (checklistState = {}) => {
    const stats = calculateDetailedStats(checklistState);
    const currentDate = new Date().toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    });

    const prompt = `
# 🚀 CONTEXTO COMPLETO DEL PROYECTO FIXARR
**Fecha de generación:** ${currentDate}

---

## 📋 RESUMEN EJECUTIVO

**Fixarr** es un ecosistema completo de gestión de servicios técnicos de electrodomésticos. El proyecto incluye múltiples plataformas interconectadas que permiten la gestión integral de citas, técnicos, clientes y comunicaciones automatizadas.

### Componentes del Ecosistema:

| Plataforma | Tecnología | URL/Ubicación |
|------------|------------|---------------|
| **Panel Admin Web** | React + Vite | https://panel.fixarr.es |
| **Panel Técnico Web** | React + Vite | https://tech.fixarr.es |
| **App Cliente Flutter** | Flutter/Dart | \`client_app_flutter/\` |
| **App Técnico Flutter** | Flutter/Dart | \`tech_app_flutter/\` |
| **Roadmap Interactivo** | React | https://roadmap.fixarr.es |
| **Backend** | Supabase (PostgreSQL + Edge Functions) | Supabase Dashboard |
| **WhatsApp Bot** | Edge Function + Meta API | Webhook activo |

---

## 🎯 ESTADO ACTUAL DEL PROYECTO

### Progreso Global: ${stats.globalPercentage}%

${generatePhasesSummary(stats)}

---

## 🧪 TAREAS EN TESTING (Requieren verificación)

${generateTestingTasks(checklistState)}

---

## ⏳ PRÓXIMAS TAREAS PRIORITARIAS

${generateNextTasks()}

---

## 🔧 ARQUITECTURA TÉCNICA

### Base de Datos (Supabase PostgreSQL)
- **Tablas principales:** \`tickets\`, \`profiles\`, \`client_addresses\`, \`business_config\`, \`reviews\`
- **RLS:** Políticas activas para seguridad por rol
- **Realtime:** Habilitado para sincronización en tiempo real

### Edge Functions Desplegadas:
- \`whatsapp-bot\` - Bot conversacional 24/7
- \`ticket-autopilot-processor\` - Procesador de citas automático
- \`ticket-autopilot-timeout\` - Gestión de timeouts
- \`send-whatsapp\` - Envío de mensajes WhatsApp
- \`send-reminders\` - Recordatorios 24h antes
- \`notify-departure\` - Notificación "técnico en camino"
- \`send-email\` - Emails con Resend

### Integraciones Externas:
- **Meta WhatsApp Business API** - Comunicación con clientes
- **Mapbox** - Cálculo de rutas y tiempos de viaje
- **Resend** - Envío de emails transaccionales
- **Vercel** - Hosting de aplicaciones web

---

## 📁 ESTRUCTURA DEL PROYECTO

\`\`\`
tech_service_ecosystem/
├── admin_panel_web/          # Panel administración (React)
├── tech_panel_web/           # Panel técnicos (React)
├── client_web_portal/        # Portal clientes web
├── client_app_flutter/       # App clientes móvil
├── tech_app_flutter/         # App técnicos móvil
├── supabase/
│   └── functions/            # Edge Functions
├── migrations/               # Scripts SQL
└── fixarr-roadmap/           # Roadmap interactivo
\`\`\`

---

## 🔐 ACCESOS Y CREDENCIALES

| Servicio | Dashboard |
|----------|-----------|
| Supabase | https://supabase.com/dashboard/project/zapjbtgnmxkhpfykxmnh |
| Vercel | https://vercel.com/redachaudri-sources-projects |
| Meta Developer | https://developers.facebook.com/apps/ |
| Mapbox | https://account.mapbox.com/ |
| Resend | https://resend.com/api-keys |
| Vault (APIs) | https://roadmap.fixarr.es/vault |

---

## 💡 CONTEXTO IMPORTANTE

### Secretaría Virtual (Bot PRO)
El sistema de "Secretaria Virtual" es el core del proyecto. Funciona así:
1. Cliente escribe por WhatsApp
2. Bot recoge datos automáticamente (nombre, dirección, problema)
3. Sistema consulta disponibilidad de técnicos por código postal
4. Bot propone 1-3 opciones de cita al cliente
5. Cliente elige → Sistema reserva automáticamente
6. Notificaciones automáticas en cada cambio de estado

### Regla de Oro de Disponibilidad
- Si hay pocos huecos: ofrece 1 opción
- Si hay disponibilidad media: ofrece 2 opciones
- Si hay mucha disponibilidad: ofrece 3 opciones

---

## 📌 INSTRUCCIONES PARA CONTINUAR

Para retomar el trabajo en este proyecto:

1. **Ubicación del código:** \`C:/Users/PC/.gemini/antigravity/scratch/tech_service_ecosystem/\`
2. **Roadmap en vivo:** https://roadmap.fixarr.es
3. **Vault con APIs:** https://roadmap.fixarr.es/vault (se entra con la sesión de GitHub del dueño)

### Comandos útiles:
\`\`\`bash
# Panel Admin
cd admin_panel_web && npm run dev

# Desplegar a producción
vercel --prod

# Ejecutar SQL en Supabase
node run_sql_file.cjs ../migrations/archivo.sql
\`\`\`

---

## ⚠️ NOTAS IMPORTANTES

- El proyecto usa **Supabase** como backend único
- Las Edge Functions se despliegan con \`supabase functions deploy\`
- Los dominios están en **Hostinger** apuntando a Vercel
- El código Flutter está preparado pero las apps móviles aún no están en producción

---

**¿Listo para continuar? Indica qué tarea quieres abordar o pregunta lo que necesites.**
`;

    return prompt.trim();
};

/**
 * Calculate detailed statistics from roadmap data
 */
const calculateDetailedStats = (checklistState) => {
    let totalTasks = 0;
    let completedTasks = 0;
    let testingTasks = 0;
    let inProgressTasks = 0;
    let pendingTasks = 0;
    const phaseStats = [];

    roadmapData.forEach((phase, index) => {
        let phaseCompleted = 0;
        let phaseTesting = 0;
        let phaseProgress = 0;
        let phasePending = 0;

        phase.tasks.forEach(task => {
            totalTasks++;

            // Check real status based on checklist
            let realStatus = task.status;
            if (task.checklist && task.checklist.length > 0) {
                const taskChecklist = checklistState[task.id] || {};
                const completedItems = task.checklist.filter(item => taskChecklist[item.id]).length;
                if (completedItems === task.checklist.length) {
                    realStatus = 'completed';
                } else if (completedItems > 0 || task.status === 'testing') {
                    realStatus = 'testing';
                }
            }

            switch (realStatus) {
                case 'completed':
                    completedTasks++;
                    phaseCompleted++;
                    break;
                case 'testing':
                    testingTasks++;
                    phaseTesting++;
                    break;
                case 'progress':
                    inProgressTasks++;
                    phaseProgress++;
                    break;
                default:
                    pendingTasks++;
                    phasePending++;
            }
        });

        phaseStats.push({
            name: phase.name,
            total: phase.tasks.length,
            completed: phaseCompleted,
            testing: phaseTesting,
            progress: phaseProgress,
            pending: phasePending,
            percentage: Math.round((phaseCompleted / phase.tasks.length) * 100)
        });
    });

    return {
        totalTasks,
        completedTasks,
        testingTasks,
        inProgressTasks,
        pendingTasks,
        globalPercentage: Math.round((completedTasks / totalTasks) * 100),
        phaseStats
    };
};

/**
 * Generate phases summary markdown
 */
const generatePhasesSummary = (stats) => {
    return stats.phaseStats.map(phase => {
        const icon = phase.percentage === 100 ? '✅' : phase.percentage > 0 ? '🔄' : '⬜';
        const bar = generateProgressBar(phase.percentage);
        return `### ${icon} ${phase.name}
${bar} **${phase.percentage}%** (${phase.completed}/${phase.total} tareas)
- Completadas: ${phase.completed} | Testing: ${phase.testing} | Pendientes: ${phase.pending}`;
    }).join('\n\n');
};

/**
 * Generate ASCII progress bar
 */
const generateProgressBar = (percentage) => {
    const filled = Math.round(percentage / 10);
    const empty = 10 - filled;
    return `[${'█'.repeat(filled)}${'░'.repeat(empty)}]`;
};

/**
 * Generate testing tasks list
 */
const generateTestingTasks = (checklistState) => {
    const testingItems = [];

    roadmapData.forEach(phase => {
        phase.tasks.forEach(task => {
            if (task.status === 'testing' || (task.checklist && task.checklist.length > 0)) {
                const taskChecklist = checklistState[task.id] || {};
                const completedItems = task.checklist ? task.checklist.filter(item => taskChecklist[item.id]).length : 0;
                const totalItems = task.checklist ? task.checklist.length : 0;

                if (task.status === 'testing' || (totalItems > 0 && completedItems < totalItems)) {
                    testingItems.push({
                        id: task.id,
                        name: task.name,
                        progress: totalItems > 0 ? `${completedItems}/${totalItems}` : 'Sin checklist',
                        percentage: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0
                    });
                }
            }
        });
    });

    if (testingItems.length === 0) {
        return '_No hay tareas en testing actualmente._';
    }

    return testingItems.map(item =>
        `- **${item.id}:** ${item.name} → Checklist: ${item.progress} (${item.percentage}%)`
    ).join('\n');
};

/**
 * Generate next priority tasks
 */
const generateNextTasks = () => {
    const nextTasks = [];

    for (const phase of roadmapData) {
        for (const task of phase.tasks) {
            if (task.status === 'pending' || task.status === 'progress') {
                nextTasks.push({
                    id: task.id,
                    name: task.name,
                    status: task.status,
                    tech: task.tech,
                    time: task.time
                });
                if (nextTasks.length >= 5) break;
            }
        }
        if (nextTasks.length >= 5) break;
    }

    if (nextTasks.length === 0) {
        return '_Todas las tareas están completadas o en testing._';
    }

    return nextTasks.map(task => {
        const icon = task.status === 'progress' ? '🔄' : '⬜';
        return `- ${icon} **${task.id}:** ${task.name}
  - Tech: ${task.tech} | Estimación: ${task.time}`;
    }).join('\n');
};

export default {
    generateContextPrompt
};
